import { useTravlData } from './TravlContext';
import { memo, useMemo, useState } from 'react';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';

type Direction = 'Arrivals' | 'Departures' | 'Totals';

const TIME_BANDS = [
    '0000-0100', '0100-0200', '0200-0300', '0300-0400', '0400-0500', '0500-0600',
    '0600-0700', '0700-0800', '0800-0900', '0900-1000', '1000-1100', '1100-1200',
    '1200-1300', '1300-1400', '1400-1500', '1500-1600', '1600-1700', '1700-1800',
    '1800-1900', '1900-2000', '2000-2100', '2100-2200', '2200-2300', '2300-2400'
] as const;

const TRAVL_MODE_DESC = [
    'Bus', 'Car', 'All Car Drivers', 'Car Driver (alone)', 'Car Driver (with pass)', 'Car Passenger', 'Coach', 'DLR',
    'HGV', 'Motor Cycle', 'Other', 'Park and Ride Bus', 'Pedal Cycle', 'Rail', 'River Boat', 'Taxi', 'Taxi Occupants',
    'Tram', 'Underground', 'Unknown', 'Walk/PT', 'Walk'
] as const;

const modeCodeMap: Record<string, (typeof TRAVL_MODE_DESC)[number]> = {
    BUS: 'Bus', CAR: 'Car', CARA: 'All Car Drivers', CARD: 'Car Driver (alone)',
    CARM: 'Car Driver (with pass)', CARP: 'Car Passenger', COAC: 'Coach', DLR: 'DLR',
    HGV: 'HGV', MOTC: 'Motor Cycle', OTHR: 'Other', PARB: 'Park and Ride Bus', PEDC: 'Pedal Cycle',
    RAIL: 'Rail', RBOT: 'River Boat', TAXI: 'Taxi', TAXO: 'Taxi Occupants', TRAM: 'Tram',
    TUBE: 'Underground', UNKW: 'Unknown', WALK: 'Walk/PT', WALO: 'Walk'
};

const toHourBand = (() => {
    const cache = new Map<string, string>();
    return (band: string) => {
        if (cache.has(band)) return cache.get(band)!;
        const m = band.match(/^(\d{1,2}):\d{2}-(\d{1,2}):\d{2}$/);
        if (!m) { cache.set(band, band); return band; }
        const startHour = m[1].padStart(2, '0');
        const endHour = (parseInt(startHour, 10) + 1).toString().padStart(2, '0');
        const val = `${startHour}00-${endHour}00`;
        cache.set(band, val);
        return val;
    };
})();

function initMatrix() {
    const base: Record<string, Record<string, number>> = {};
    for (const band of TIME_BANDS) base[band] = {};
    return base;
}

export default function ExtractionTable({ surveyCode }: { surveyCode: string; }) {
    const { surveyData, siteData } = useTravlData();
    const [direction, setDirection] = useState<Direction>('Totals');

    const siteName = useMemo(() => {
        const map = new Map(siteData.map((s: any) => [s.SurveyCode, s.Name]));
        return map.get(surveyCode) ?? '';
    }, [siteData, surveyCode]);

    const filtered = useMemo(() => {
        if (!surveyData?.length) return [];
        return surveyData.filter((e: any) => e.SurveyCode === surveyCode);
    }, [surveyData, surveyCode]);

    const aggregates = useMemo(() => {
        const arrivals = initMatrix();
        const departures = initMatrix();

        for (let i = 0; i < filtered.length; i++) {
            const e = filtered[i];
            const band = toHourBand(e.TimeBand);
            const mode = modeCodeMap[e.ModeCode] ?? e.ModeCode;
            if (!TRAVL_MODE_DESC.includes(mode as any)) continue;

            const isOcc = e.ModeCode === 'CARP' || e.ModeCode === 'TAXO';
            const inField = isOcc ? 'OccIn' : 'JnysIn';
            const outField = isOcc ? 'OccOut' : 'JnysOut';
            const inVal = parseInt(e[inField] ?? '0', 10) || 0;
            const outVal = parseInt(e[outField] ?? '0', 10) || 0;

            arrivals[band][mode] = (arrivals[band][mode] ?? 0) + inVal;
            departures[band][mode] = (departures[band][mode] ?? 0) + outVal;
        }

        const totals = initMatrix();
        for (const band of TIME_BANDS) {
            for (const mode of TRAVL_MODE_DESC) {
                const a = arrivals[band][mode] ?? 0;
                const d = departures[band][mode] ?? 0;
                if (a || d) totals[band][mode] = a + d;
            }
        }
        return { arrivals, departures, totals };
    }, [filtered]);

    const matrix = useMemo(() => {
        if (direction === 'Arrivals') return aggregates.arrivals;
        if (direction === 'Departures') return aggregates.departures;
        return aggregates.totals;
    }, [direction, aggregates]);

    const DataRow = memo(function DataRow({
        band, row,
    }: {
        band: (typeof TIME_BANDS)[number];
        row: Record<(typeof TRAVL_MODE_DESC)[number], number | undefined>;
    }) {
        return (
            <TableRow>
                <TableCell>{band}</TableCell>
                {TRAVL_MODE_DESC.map((mode) => (
                    <TableCell key={mode} align="right">{row[mode] ?? ''}</TableCell>
                ))}
            </TableRow>
        );
    });

    return (
        <Paper>
            <Box sx={{ p: 2, pb: 0 }}>
                <Typography variant="h6">{siteName || 'Extraction'}</Typography>
            </Box>

            <Box sx={{ px: 2, pt: 1 }}>
                <FormControl>
                    <FormLabel>Direction</FormLabel>
                    <RadioGroup
                        row
                        value={direction}
                        onChange={(e) => setDirection(e.target.value as Direction)}
                    >
                        <FormControlLabel value="Arrivals" control={<Radio />} label="Arrivals" />
                        <FormControlLabel value="Departures" control={<Radio />} label="Departures" />
                        <FormControlLabel value="Totals" control={<Radio />} label="Totals" />
                    </RadioGroup>
                </FormControl>
            </Box>

            <TableContainer>
                <Table size="small" sx={{ 'td, th': { padding: '4px', fontSize: '0.73rem', whiteSpace: 'nowrap', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Time Period</TableCell>
                            {TRAVL_MODE_DESC.map((m) => <TableCell key={m} align="right">{m}</TableCell>)}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {TIME_BANDS.map((band) => <DataRow key={band} band={band} row={matrix[band]} />)}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}
